import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { api } from "../../lib/api";
import {
  formatINR,
  getFareInfoRows,
  getSearchPayloadRows,
  getActualPayloadRows,
  getFareDetailRows,
  displayValue as formatPayloadValue,
} from "../../utils/bookingDetailParsers";

function formatVal(val) {
  if (val === null || val === undefined) return "-";
  if (typeof val === "object") return JSON.stringify(val);
  return String(val);
}

function isPnrEmpty(pnr) {
  return pnr === null || pnr === undefined || String(pnr).trim() === "";
}

const isMultiCityBooking = (b) => Number(b?.journey_type) === 3;

// Multi city tabs, one per trip, ordered Trip 1, 2, 3... A multi city booked on
// one PNR is a single row whose `trips` (from its segments) become one tab
// each; a multi city stored as one row per trip gets one tab per row. A
// retried trip has more than one row, so a repeat gets a running count
// ("Trip 2 (2)") to keep every tab distinct.
function multiCityTrips(list) {
  const entries = [];
  list.forEach((b) => {
    if (Array.isArray(b.trips) && b.trips.length) {
      b.trips.forEach((trip) => entries.push({ booking: b, trip, n: Number(trip.trip_indicator) || null }));
    } else {
      entries.push({ booking: b, trip: null, n: Number(b.trip_indicator_id) || null });
    }
  });
  entries.sort(
    (a, b) => (a.n ?? 999) - (b.n ?? 999) || new Date(a.booking.created_at) - new Date(b.booking.created_at)
  );
  const seen = {};
  return entries.map((e, idx) => {
    const n = e.n ?? idx + 1;
    seen[n] = (seen[n] ?? 0) + 1;
    return {
      key: `${e.booking.booking_id}:${n}:${seen[n]}`,
      booking: e.booking,
      trip: e.trip,
      label: seen[n] > 1 ? `Trip ${n} (${seen[n]})` : `Trip ${n}`,
    };
  });
}

function formatTripTime(val) {
  return val ? new Date(val).toLocaleString() : "-";
}

// Flights of the selected multi city trip (from the booking's segments).
function TripSegmentsTable({ trip }) {
  const segs = Array.isArray(trip?.segments) ? trip.segments : [];
  if (!segs.length) return null;
  return (
    <div className="mb-6">
      <label className="text-sm font-semibold text-gray-600 block mb-2">Trip {trip.trip_indicator} Flights</label>
      <div className="rounded-xl border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="text-left font-semibold px-3 py-2">Flight</th>
              <th className="text-left font-semibold px-3 py-2">From</th>
              <th className="text-left font-semibold px-3 py-2">Departure</th>
              <th className="text-left font-semibold px-3 py-2">To</th>
              <th className="text-left font-semibold px-3 py-2">Arrival</th>
              <th className="text-left font-semibold px-3 py-2">Duration</th>
              <th className="text-left font-semibold px-3 py-2">Cabin</th>
              <th className="text-left font-semibold px-3 py-2">Baggage</th>
              <th className="text-left font-semibold px-3 py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {segs.map((sg, idx) => (
              <tr key={idx} className="border-t">
                <td className="px-3 py-2 whitespace-nowrap">{[sg.airline_code, sg.flight_number].filter(Boolean).join(" ") || "-"}</td>
                <td className="px-3 py-2">{sg.origin ?? "-"}</td>
                <td className="px-3 py-2 whitespace-nowrap">{formatTripTime(sg.dep_time)}</td>
                <td className="px-3 py-2">{sg.destination ?? "-"}</td>
                <td className="px-3 py-2 whitespace-nowrap">{formatTripTime(sg.arr_time)}</td>
                <td className="px-3 py-2 whitespace-nowrap">{sg.duration ?? "-"}</td>
                <td className="px-3 py-2">{sg.cabin_class ?? "-"}</td>
                <td className="px-3 py-2">{sg.baggage ?? "-"}</td>
                <td className="px-3 py-2">{sg.flight_status ?? "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const fields = [
  { key: "booking_id", label: "Booking ID" },
  { key: "booking_ticket_number", label: "Booking Reference Number" },
  { key: "flight_id", label: "Booking ID", editableWhenPnrEmpty: true },
  { key: "user_id", label: "User ID" },
  { key: "trace_id", label: "Trace ID" },
  { key: "username", label: "Username" },
  { key: "payment_id", label: "Payment ID" },
  { key: "fare_type", label: "Fare Type" },
  { key: "last_ticket_date", label: "Last Ticket Date" },
  { key: "ticket_advisary", label: "Ticket Advisary" },
  { key: "airline_code", label: "Airline Code" },
  { key: "pnr", label: "PNR", editableWhenPnrEmpty: true },
  { key: "airline_pnr", label: "Airline PNR" },
  { key: "origin", label: "Origin" },
  { key: "destination", label: "Destination" },
  { key: "trip_indicator_id", label: "Trip Indicator ID" },
  { key: "journey_type", label: "Journey Type" },
  { key: "cancellation_type", label: "Cancellation Type" },
  { key: "cancellation_response", label: "Cancellation Response" },
  { key: "created_at", label: "Created At" },
];

/** Generic key-value rows from object (one level) */
function getKeyValueRows(raw) {
  if (raw == null || raw === "") return [];
  let obj = typeof raw === "string" ? (() => { try { return JSON.parse(raw); } catch { return null; } })() : raw;
  if (!obj || typeof obj !== "object") return [];
  return Object.entries(obj).map(([k, v]) => ({
    label: String(k).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
    value: v === null || v === undefined ? "-" : (typeof v === "object" ? JSON.stringify(v) : String(v)),
  }));
}

function PayloadModal({ open, onClose, type, raw, fareInfoRows, fareTotal, hasTotalInRows }) {
  if (!open) return null;

  const title =
    type === "search" ? "Booking Payload" : type === "actual" ? "Booking Payload" : type === "fare_info" ? "Fare Info" : "Fare Detail";

  let body = null;
  if (type === "fare_info") {
    body = (
      <div className="rounded-xl overflow-hidden border border-gray-200 w-full max-w-md">
        <div className="bg-sky-100 px-4 py-2.5">
          <span className="font-bold text-gray-800">Fare Summary</span>
        </div>
        {fareInfoRows.length > 0 ? (
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <tbody>
              {fareInfoRows.map((row, idx) => {
                const isTotalRow = String(row.label).toLowerCase() === "total";
                return (
                  <tr key={idx} className={isTotalRow ? "bg-gray-100" : ""}>
                    <td className={`py-2 px-4 ${isTotalRow ? "font-bold text-gray-800" : "text-gray-700"}`}>{row.label}</td>
                    <td className={`py-2 px-4 text-right tabular-nums ${isTotalRow ? "font-bold text-gray-800" : "text-gray-700"}`}>
                      {formatINR(row.value)}
                    </td>
                  </tr>
                );
              })}
              {!hasTotalInRows && fareInfoRows.length > 0 && (
                <tr className="bg-gray-100">
                  <td className="py-2.5 px-4 font-bold text-gray-800">Total</td>
                  <td className="py-2.5 px-4 text-right font-bold text-gray-800 tabular-nums">{formatINR(fareTotal)}</td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
        ) : (
          <div className="p-3 text-sm text-gray-600">No fare info</div>
        )}
      </div>
    );
  } else if (type === "search") {
    const rows = getSearchPayloadRows(raw);
    body = (
      <div className="rounded-xl overflow-hidden border border-gray-200 w-full max-h-[70vh] overflow-y-auto">
        <div className="bg-sky-100 px-4 py-2.5 sticky top-0">
          <span className="font-bold text-gray-800">Booking Payload</span>
        </div>
        <div className="overflow-x-auto">
        <table className="w-full text-sm table-fixed">
          <colgroup>
            <col className="w-[40%] min-w-[140px]" />
            <col className="w-[60%] min-w-[180px]" />
          </colgroup>
          <tbody>
            {rows.map((r, idx) => (
              <tr key={idx} className={r.isSection ? "bg-gray-200" : ""}>
                <td className={`py-2 px-4 align-top ${r.isSection ? "font-bold text-gray-800" : "text-gray-700"}`}>{r.label}</td>
                <td className="py-2 px-4 text-right text-gray-800 break-words align-top">{r.value ?? "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        {rows.length === 0 && (
          <div className="p-3 text-sm text-gray-600">No data or invalid JSON</div>
        )}
      </div>
    );
  } else if (type === "actual" || type === "fare_detail") {
    const rows = type === "actual" ? getActualPayloadRows(raw) : getFareDetailRows(raw);
    body = (
      <div className="rounded-xl overflow-hidden border border-gray-200 w-full max-h-[70vh] overflow-y-auto">
        <div className="bg-sky-100 px-4 py-2.5 sticky top-0">
          <span className="font-bold text-gray-800">{title}</span>
        </div>
        {rows.length > 0 ? (
          <div className="overflow-x-auto">
          <table className="w-full text-sm table-fixed">
            <colgroup>
              <col className="w-[40%] min-w-[140px]" />
              <col className="w-[60%] min-w-[180px]" />
            </colgroup>
            <tbody>
              {rows.map((r, idx) =>
                r.isSeparator ? (
                  <tr key={idx} className="border-t border-gray-300">
                    <td colSpan={2} className="py-1.5 bg-gray-100" aria-hidden />
                  </tr>
                ) : (
                  <tr key={idx} className={r.isSection ? "bg-gray-200" : ""}>
                    <td className={`py-2 px-4 align-top ${r.isSection ? "font-bold text-gray-800" : "text-gray-700"}`}>{r.label}</td>
                    <td className="py-2 px-4 text-right text-gray-800 break-words align-top">{r.value ?? "-"}</td>
                  </tr>
                )
              )}
            </tbody>
          </table>
          </div>
        ) : (
          <div className="p-3 text-sm">
            {raw != null && raw !== "" ? (
              <pre className="whitespace-pre-wrap break-all font-mono text-xs bg-gray-50 p-3 rounded-lg">
                {typeof raw === "string" ? raw : JSON.stringify(raw, null, 2)}
              </pre>
            ) : (
              <span className="text-gray-600">No data</span>
            )}
          </div>
        )}
      </div>
    );
  } else {
    body = (
      <div className="p-3 text-sm text-gray-600">Unknown payload type</div>
    );
  }

  const isWide = type === "search" || type === "actual" || type === "fare_detail";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className={`bg-white rounded-2xl shadow-xl w-full max-h-[90vh] flex flex-col ${isWide ? "max-w-4xl" : "max-w-2xl"}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50 rounded-t-2xl">
          <h3 className="text-lg font-bold text-gray-800">{title}</h3>
          <button type="button" onClick={onClose} className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-semibold hover:bg-gray-100">
            Close
          </button>
        </div>
        <div className="p-4 overflow-auto flex-1 min-h-0 w-full">{body}</div>
      </div>
    </div>
  );
}

function SingleBookingContent({ data, editFlightId, setEditFlightId, editPnr, setEditPnr, editAirlinePnr, setEditAirlinePnr, validationErrors, setValidationErrors, canEditPnr, handleSubmitForm, handleConfirmSave, handleCloseConfirm, confirmOpen, saving, nameMismatch, onOpenPayload, trip = null }) {
  return (
    <>
      {canEditPnr && (
        <form onSubmit={handleSubmitForm} className="mb-6 p-4 border rounded-xl bg-gray-50 border-amber-200">
          <div className="text-sm font-semibold text-gray-700 mb-3">Edit TBO Reference ID &amp; PNR (only when PNR is empty)</div>
          <p className="text-xs text-gray-600 mb-3">Provide either PNR or Airline PNR (or both)</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-semibold text-gray-600">TBO Reference ID <span className="text-red-500">*</span></label>
              <input
                type="text"
                className={`mt-1 w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black ${validationErrors.flightId ? "border-red-500" : ""}`}
                value={editFlightId}
                onChange={(e) => {
                  setEditFlightId(e.target.value);
                  if (validationErrors.flightId) setValidationErrors((p) => ({ ...p, flightId: "" }));
                }}
                placeholder="TBO Reference ID"
              />
              {validationErrors.flightId && (
                <p className="mt-1 text-xs text-red-600">{validationErrors.flightId}</p>
              )}
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-600">PNR</label>
              <input
                type="text"
                className={`mt-1 w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black ${validationErrors.pnr ? "border-red-500" : ""}`}
                value={editPnr}
                onChange={(e) => {
                  setEditPnr(e.target.value);
                  if (validationErrors.pnr) setValidationErrors((p) => ({ ...p, pnr: "" }));
                }}
                placeholder="PNR (or provide Airline PNR)"
              />
              {validationErrors.pnr && (
                <p className="mt-1 text-xs text-red-600">{validationErrors.pnr}</p>
              )}
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-600">Airline PNR</label>
              <input
                type="text"
                className={`mt-1 w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black ${validationErrors.airlinePnr ? "border-red-500" : ""}`}
                value={editAirlinePnr}
                onChange={(e) => {
                  setEditAirlinePnr(e.target.value);
                  if (validationErrors.airlinePnr) setValidationErrors((p) => ({ ...p, airlinePnr: "" }));
                }}
                placeholder="Airline PNR (optional)"
              />
              {validationErrors.airlinePnr && (
                <p className="mt-1 text-xs text-red-600">{validationErrors.airlinePnr}</p>
              )}
            </div>
          </div>
          <div className="mt-3">
            <button
              type="submit"
              disabled={saving}
              className="rounded-xl bg-black px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
            >
              Save TBO Reference ID & PNR
            </button>
          </div>
        </form>
      )}

      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-lg max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-2">Confirm Save</h3>
            <p className="text-sm text-gray-600 mb-4">
              This looks up the booking with TBO and, if it matches, recovers it in full:
              fare details, passengers, and segments are all updated from TBO&apos;s confirmed
              data. Airline PNR isn&apos;t taken from the field above, it&apos;s set from
              TBO&apos;s response.
            </p>
            <div className="space-y-2 mb-4 p-4 bg-gray-50 rounded-xl text-sm">
              <div className="flex justify-between gap-4">
                <span className="font-semibold text-gray-600">TBO Reference ID:</span>
                <span className="text-gray-900 font-medium">{(editFlightId ?? "").trim() || "-"}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="font-semibold text-gray-600">PNR:</span>
                <span className="text-gray-900 font-medium">{(editPnr ?? "").trim() || "-"}</span>
              </div>
            </div>

            {nameMismatch && (
              <div className="rounded-xl border border-red-300 bg-red-50 p-3 text-sm text-red-800 mb-4">
                Passenger name mismatch with TBO. The booking details cannot be saved.
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleCloseConfirm}
                disabled={saving}
                className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              {!nameMismatch && (
                <button
                  type="button"
                  onClick={handleConfirmSave}
                  disabled={saving}
                  className="rounded-xl bg-black px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
                >
                  {saving ? "Saving..." : "Confirm & Save"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {fields.map(({ key, label, editableWhenPnrEmpty }) => {
          const isEditableField = editableWhenPnrEmpty && canEditPnr;
          let displayValue;
          if (isEditableField) {
            displayValue = key === "flight_id" ? editFlightId : key === "pnr" ? editPnr : formatVal(data[key]);
          } else if (key === "cancellation_response") {
            displayValue =
              typeof data[key] === "object" ? JSON.stringify(data[key], null, 2) : formatVal(data[key]);
          } else if (key === "created_at") {
            displayValue = data[key] ? new Date(data[key]).toLocaleString() : "-";
          } else if (key === "trip_indicator_id" && trip) {
            displayValue = `Trip ${trip.trip_indicator}`;
          } else if (key === "trip_indicator_id" && isMultiCityBooking(data) && data[key] != null) {
            displayValue = `Trip ${data[key]}`;
          } else if ((key === "origin" || key === "destination") && trip?.[key]) {
            displayValue = trip[key];
          } else {
            displayValue = formatPayloadValue(key, data[key]);
          }

          return (
            <div key={key} className="flex flex-col">
              <label className="text-sm font-semibold text-gray-600">{label}</label>
              <div className="mt-1 p-3 rounded-xl bg-gray-50 text-sm border min-h-[44px]">
                {displayValue || "-"}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6">
        {trip && <TripSegmentsTable trip={trip} />}
        <label className="text-sm font-semibold text-gray-600 block mb-2">Booking Details</label>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!data?.search_payload}
            onClick={() => onOpenPayload("search", data?.search_payload)}
          >
            User Booking Data
          </button>
          <button
            type="button"
            className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!data?.actual_payload}
            onClick={() => onOpenPayload("actual", data?.actual_payload)}
          >
            Booking Passenger Details
          </button>
          <button
            type="button"
            className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!data?.fare_info}
            onClick={() => onOpenPayload("fare_info", data?.fare_info)}
          >
            Booking Fare Info Summary
          </button>
          <button
            type="button"
            className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!data?.fare_detail}
            onClick={() => onOpenPayload("fare_detail", data?.fare_detail)}
          >
            Search Fare Detail 
          </button>
        </div>
      </div>
    </>
  );
}

export default function BookingViewEditPage() {
  const { bookingId, traceId } = useParams();
  const navigate = useNavigate();
  const [bookingsByTrace, setBookingsByTrace] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [activeTab, setActiveTab] = useState("onward");
  // Multi city only: key of the selected "Trip N" tab (null = first trip).
  const [activeTripKey, setActiveTripKey] = useState(null);
  const [editFlightId, setEditFlightId] = useState("");
  const [editPnr, setEditPnr] = useState("");
  const [editAirlinePnr, setEditAirlinePnr] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [nameMismatch, setNameMismatch] = useState(null);
  const [validationErrors, setValidationErrors] = useState({ flightId: "", pnr: "", airlinePnr: "" });
  const [payloadModal, setPayloadModal] = useState({ open: false, type: null, raw: null });
  const [sendingEticket, setSendingEticket] = useState(false);

  const canEditPnr = data ? isPnrEmpty(data.pnr) : false;
  const activeBookingId = data?.booking_id ?? null;
  const canSendEticket = data ? Number(data.status) === 1 && !isPnrEmpty(data.pnr) : false;

  const fareInfoRows = data ? getFareInfoRows(data.fare_info) : [];
  const fareTotal = fareInfoRows.reduce((sum, r) => sum + r.value, 0);
  const hasTotalInRows = fareInfoRows.some((r) => String(r.label).toLowerCase() === "total");

  const openPayload = (type, raw) => {
    setPayloadModal({ open: true, type, raw });
  };

  const validateForm = () => {
    const flightIdTrim = (editFlightId ?? "").trim();
    const pnrTrim = (editPnr ?? "").trim();
    const airlinePnrTrim = (editAirlinePnr ?? "").trim();
    const errors = { flightId: "", pnr: "", airlinePnr: "" };
    if (!flightIdTrim) errors.flightId = "TBO Reference ID is required";
    if (!pnrTrim && !airlinePnrTrim) errors.pnr = "PNR or Airline PNR is required";
    setValidationErrors(errors);
    return !errors.flightId && !errors.pnr;
  };

  useEffect(() => {
    if (traceId) {
      setLoading(true);
      setErr("");
      api
        .get(`/admin/bookings/by-trace/${traceId}`)
        .then((res) => {
          const list = res.data?.data || [];
          setBookingsByTrace(list);
          if (list.length === 0) {
            setData(null);
          } else if (list.length === 1) {
            setData(list[0]);
            setEditFlightId(list[0].flight_id ?? "");
            setEditPnr(list[0].pnr ?? "");
            setEditAirlinePnr(list[0].airline_pnr ?? "");
          } else {
            const onward = list.find((b) => b.trip_indicator_id == 1);
            const ret = list.find((b) => b.trip_indicator_id == 2);
            const current = activeTab === "return" && ret ? ret : (onward || list[0]);
            setData(current);
            setEditFlightId(current.flight_id ?? "");
            setEditPnr(current.pnr ?? "");
            setEditAirlinePnr(current.airline_pnr ?? "");
          }
        })
        .catch((e) => {
          const msg = e?.response?.data?.message || e.message || "Failed to load bookings";
          setErr(msg);
          toast.error(msg);
        })
        .finally(() => setLoading(false));
    } else if (bookingId) {
      setLoading(true);
      setErr("");
      api
        .get(`/admin/bookings/${bookingId}`)
        .then((res) => {
          const d = res.data?.data || null;
          setData(d);
          setBookingsByTrace(d ? [d] : []);
          if (d) {
            setEditFlightId(d.flight_id ?? "");
            setEditPnr(d.pnr ?? "");
            setEditAirlinePnr(d.airline_pnr ?? "");
          }
        })
        .catch((e) => {
          const msg = e?.response?.data?.message || e.message || "Failed to load booking";
          setErr(msg);
          toast.error(msg);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
      setData(null);
      setBookingsByTrace([]);
    }
  }, [traceId, bookingId]);

  useEffect(() => {
    if (bookingsByTrace.length > 1 && bookingsByTrace.some(isMultiCityBooking)) {
      const trips = multiCityTrips(bookingsByTrace);
      const current = (trips.find((t) => t.key === activeTripKey) || trips[0]).booking;
      setData(current);
      setEditFlightId(current.flight_id ?? "");
      setEditPnr(current.pnr ?? "");
      setEditAirlinePnr(current.airline_pnr ?? "");
    } else if (bookingsByTrace.length > 1) {
      const onward = bookingsByTrace.find((b) => b.trip_indicator_id == 1);
      const ret = bookingsByTrace.find((b) => b.trip_indicator_id == 2);
      const current = activeTab === "return" && ret ? ret : (onward || bookingsByTrace[0]);
      setData(current);
      setEditFlightId(current.flight_id ?? "");
      setEditPnr(current.pnr ?? "");
      setEditAirlinePnr(current.airline_pnr ?? "");
    }
  }, [activeTab, activeTripKey, bookingsByTrace]);

  useEffect(() => {
    if (data) {
      setEditFlightId(data.flight_id ?? "");
      setEditPnr(data.pnr ?? "");
      setEditAirlinePnr(data.airline_pnr ?? "");
    }
  }, [data?.booking_id]);

  const handleSubmitForm = (e) => {
    e.preventDefault();
    if (!activeBookingId || !canEditPnr) return;
    if (!validateForm()) {
      toast.error("Please fix the errors before saving.");
      return;
    }
    setNameMismatch(null);
    setConfirmOpen(true);
  };

  const handleCloseConfirm = () => {
    setConfirmOpen(false);
    setNameMismatch(null);
  };

  /** Re-fetches the booking (and, in trace mode, all its siblings) after a
   *  recovery, since applyRecoveredBookingData can touch fare/origin/destination/
   *  status fields beyond the three identifiers this form edits directly. */
  const refetchAfterRecovery = async () => {
    if (traceId) {
      const traceRes = await api.get(`/admin/bookings/by-trace/${traceId}`);
      const list = traceRes.data?.data || [];
      setBookingsByTrace(list);
      setData(list.find((b) => b.booking_id === activeBookingId) || list[0] || null);
    } else {
      const bookingRes = await api.get(`/admin/bookings/${activeBookingId}`);
      const refreshed = bookingRes.data?.data || null;
      setData(refreshed);
      setBookingsByTrace(refreshed ? [refreshed] : []);
    }
  };

  const handleConfirmSave = async () => {
    if (!activeBookingId || !canEditPnr) return;
    setSaving(true);
    try {
      await api.post(`/admin/bookings/${activeBookingId}/recover-pnr`, {
        pnr: (editPnr ?? "").trim() || null,
        tbo_booking_id: (editFlightId ?? "").trim() || null,
      });
      await refetchAfterRecovery();
      setNameMismatch(null);
      setConfirmOpen(false);
      toast.success("Booking recovered from TBO. Fare, passengers, and segments have been updated.");
    } catch (e) {
      const reason = e?.response?.data?.reason;
      if (reason === "NAME_MISMATCH") {
        setNameMismatch({
          mismatches: e.response.data.mismatches || [],
          tboPassengers: e.response.data.tbo_passengers || [],
        });
      } else {
        const msg = e?.response?.data?.message || e.message || "Recovery failed";
        toast.error(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSendEticket = async () => {
    if (!activeBookingId) return;
    setSendingEticket(true);
    try {
      const res = await api.post(`/admin/bookings/${activeBookingId}/send-eticket`);
      toast.success(res.data?.message || "E-ticket email sent");
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || "Failed to send e-ticket";
      toast.error(msg);
    } finally {
      setSendingEticket(false);
    }
  };

  const hasTabs = bookingsByTrace.length > 1;
  const multiCityTabs = bookingsByTrace.some(isMultiCityBooking) ? multiCityTrips(bookingsByTrace) : [];
  const tripTabs = multiCityTabs.length > 1 ? multiCityTabs : null;
  const activeTrip = tripTabs ? tripTabs.find((t) => t.key === activeTripKey) || tripTabs[0] : null;
  const onwardBooking = bookingsByTrace.find((b) => b.trip_indicator_id == 1);
  const returnBooking = bookingsByTrace.find((b) => b.trip_indicator_id == 2);

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold">Booking Details</h1>
        <div className="flex items-center gap-2">
          {canSendEticket && (
            <button
              type="button"
              onClick={handleSendEticket}
              disabled={sendingEticket}
              className="rounded-xl border border-black bg-black px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
            >
              {sendingEticket ? "Sending..." : "Send E-ticket"}
            </button>
          )}
          <button
            type="button"
            onClick={() => navigate("/admin/bookings")}
            className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50"
          >
            Back to List
          </button>
        </div>
      </div>

      {err ? (
        <div className="text-sm text-red-600">{err}</div>
      ) : loading ? (
        <div className="text-sm text-gray-600">Loading...</div>
      ) : !data ? (
        <div className="text-sm text-gray-600">Booking not found.</div>
      ) : (
        <div className="border rounded-2xl overflow-hidden bg-white">
          <div className="bg-gray-50 px-4 py-3 text-sm font-semibold border-b flex items-center justify-between flex-wrap gap-2">
            <span>Booking: {data.booking_ticket_number}</span>
            {tripTabs ? (
              <div className="flex flex-wrap rounded-lg border border-gray-300 overflow-hidden">
                {tripTabs.map(({ key, label }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setActiveTripKey(key)}
                    className={`px-4 py-2 text-sm font-semibold ${activeTrip?.key === key ? "bg-black text-white" : "bg-white text-gray-700 hover:bg-gray-50"}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            ) : hasTabs && (
              <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setActiveTab("onward")}
                  className={`px-4 py-2 text-sm font-semibold ${activeTab === "onward" ? "bg-black text-white" : "bg-white text-gray-700 hover:bg-gray-50"}`}
                >
                  Onward
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab("return")}
                  className={`px-4 py-2 text-sm font-semibold ${activeTab === "return" ? "bg-black text-white" : "bg-white text-gray-700 hover:bg-gray-50"}`}
                >
                  Return
                </button>
              </div>
            )}
            {canEditPnr && (
              <span className="text-xs font-normal text-amber-700 bg-amber-100 px-2 py-1 rounded">
                PNR is empty — you can edit TBO Reference ID and PNR below
              </span>
            )}
          </div>
          <div className="p-6">
            <SingleBookingContent
              data={data}
              editFlightId={editFlightId}
              setEditFlightId={setEditFlightId}
              editPnr={editPnr}
              setEditPnr={setEditPnr}
              editAirlinePnr={editAirlinePnr}
              setEditAirlinePnr={setEditAirlinePnr}
              validationErrors={validationErrors}
              setValidationErrors={setValidationErrors}
              canEditPnr={canEditPnr}
              handleSubmitForm={handleSubmitForm}
              handleConfirmSave={handleConfirmSave}
              handleCloseConfirm={handleCloseConfirm}
              confirmOpen={confirmOpen}
              saving={saving}
              nameMismatch={nameMismatch}
              onOpenPayload={openPayload}
              trip={activeTrip?.trip ?? null}
            />
          </div>
        </div>
      )}

      <PayloadModal
        open={payloadModal.open}
        onClose={() => setPayloadModal({ open: false, type: null, raw: null })}
        type={payloadModal.type}
        raw={payloadModal.raw}
        fareInfoRows={fareInfoRows}
        fareTotal={fareTotal}
        hasTotalInRows={hasTotalInRows}
      />
    </div>
  );
}
